// web/src/lib/ignitionFx.ts
// Tiny external store that fires the app-root <IgnitionOverlay>: the dramatic iris-explosion
// transition when a generation completes. `trigger()` bumps a nonce (re-runs the overlay's
// animation effect) and carries the blast origin (viewport px, usually the cover center) plus
// the route to navigate to AT THE BLAST PEAK (under the full-white flash). `to: null` runs the
// visual only (for the /dev/loader tuner). Mirrors the loaderFx/wordmarkFx store pattern.
import { useSyncExternalStore } from "react";

export interface IgnitionState {
  nonce: number; // increments per trigger; the overlay keys its animation on it
  originX: number; // blast center, viewport px
  originY: number;
  to: string | null; // navigate here at the blast peak; null = visual only
  arrivalAt: number; // monotonic ms of the last navigating trigger (0 = none); gates the Player impact entrance
}

/** Blast timeline (ms). expand = the detonation, hold = full-white beat, fade = the reveal. */
export const IGNITION_TIMING = { expandMs: 300, holdMs: 90, fadeMs: 480 } as const;

/** How recently a navigating trigger must have fired for a mounting screen to play the impact entrance. */
export const ARRIVAL_WINDOW_MS = 2500;

const nowMs = (): number => (typeof performance !== "undefined" ? performance.now() : 0);

let _state: IgnitionState = { nonce: 0, originX: 0, originY: 0, to: null, arrivalAt: 0 };
const subs = new Set<() => void>();

export const ignitionFx = {
  get: (): IgnitionState => _state,
  /** Fire the transition. Origins default to viewport center / upper-third. A truthy `to`
   *  marks an arrival (stamps arrivalAt) so the destination screen plays its impact entrance. */
  trigger: (opts: { originX?: number; originY?: number; to?: string | null } = {}): void => {
    const w = typeof window !== "undefined" ? window.innerWidth : 0;
    const h = typeof window !== "undefined" ? window.innerHeight : 0;
    const to = opts.to ?? null;
    _state = {
      nonce: _state.nonce + 1,
      originX: opts.originX ?? w / 2,
      originY: opts.originY ?? h * 0.4,
      to,
      arrivalAt: to ? nowMs() : 0,
    };
    for (const cb of subs) cb();
  },
  subscribe: (cb: () => void): (() => void) => { subs.add(cb); return () => { subs.delete(cb); }; },
  /** True if a navigating trigger fired within ARRIVAL_WINDOW_MS (a fresh iris-explosion arrival). */
  isFreshArrival: (): boolean => _state.arrivalAt > 0 && nowMs() - _state.arrivalAt < ARRIVAL_WINDOW_MS,
};

/** Reactive read of the ignition trigger state. */
export function useIgnitionFx(): IgnitionState {
  return useSyncExternalStore(ignitionFx.subscribe, ignitionFx.get, () => _state);
}
